/**
 * # AnchoredFixedPosition
 *
 * A component that positions its children relative to a target element using the Popper library.
 * An wrapper implementation of the `react-popper` library. Please refer to the [React Popper Docs](https://popper.js.org/docs/v1/#Popper) for more typing details.
 *
 * ## Props extends IPopperProps
 *
 * - `className?: string`: Additional CSS class name for the component.
 * - `target?: HTMLElement`: The target element to position the children relative to.
 * - `eventsEnabled?: boolean`: Whether events are enabled for the Popper component.
 * - `children?: React.ReactNode`: The content to be rendered inside the component.
 * - `placement?: typeof placements[number]`: The placement of the component relative to the target element.
 * - `usePortal?: boolean`: Whether to render the component inside a portal. If `true`, effectively takes element outside of its natural DOM-placement and overlays it above the document.
 * - `arrowClassName?: string`: When provided, renders a Popper `Arrow` element with this class inside the popper node. Popper's arrow modifier positions it along the popper edge so it points at the target.
 * - `...rest: IPopperProps`: Any member of `IPopperProps`
 *
 * ## Usage
 *
 * ```tsx
 * import AnchoredFixedPosition from 'path/to/AnchoredFixedPosition';
 *
 * const ExampleComponent = () => {
 *   const targetRef = useRef(null);
 *
 *   return (
 *     <div>
 *       <button ref={targetRef}>Target Element</button>
 *       <AnchoredFixedPosition target={targetRef.current} placement="bottom">
 *         <div>Positioned Content</div>
 *       </AnchoredFixedPosition>
 *     </div>
 *   );
 * };
 * ```
 */
import React from 'react';
import type PopperJS from 'popper.js';
import type {IPopperProps, placements} from 'react-popper';
import {Arrow, Popper} from 'react-popper';
import {AppPortal} from 'client/Portals/AppPortal/AppPortal.react';

interface AnchoredFixedPositonProps extends OmitChildren<IPopperProps>, PropsWithClassNameOptional {
  children?: React.ReactNode | (() => React.ReactNode);
  target?: HTMLElement;
  eventsEnabled?: boolean;
  placement?: (typeof placements)[number];
  usePortal?: boolean;
  arrowClassName?: string;
}

/**
 * How far (px) the arrow must stay from either end of the popper. Popper's own
 * `arrow` modifier only clamps the arrow's layout box to the popper's bounding box,
 * but our caret is a rotated square whose corners overhang that box (~2px for a
 * 10px caret), so a flush arrow renders past the popper's edge and onto its
 * rounded corners. 8px covers the overhang plus the tooltip's 4px border radius.
 */
export const ARROW_EDGE_INSET = 8;

/**
 * Custom Popper modifier that re-clamps `data.offsets.arrow` after the built-in
 * `arrow` modifier (order 500) has positioned it, keeping the arrow at least
 * ARROW_EDGE_INSET away from both ends of the popper.
 */
export const keepArrowWithinBounds: PopperJS.ModifierFn = (data) => {
  const arrowOffsets = data.offsets.arrow;
  const arrowElement = data.arrowElement as HTMLElement | undefined;
  if (!arrowElement || !arrowOffsets) {
    return data;
  }

  // The arrow slides along the popper's horizontal edge for top/bottom
  // placements and along its vertical edge for left/right placements.
  const isVertical = ['left', 'right'].includes(data.placement.split('-')[0]);
  const side = isVertical ? 'top' : 'left';
  const length = isVertical ? 'height' : 'width';
  const arrowSize = isVertical ? arrowElement.offsetHeight : arrowElement.offsetWidth;
  const max = data.offsets.popper[length] - arrowSize - ARROW_EDGE_INSET;

  // Popper too small to inset from both ends — keep Popper's own clamping.
  if (max < ARROW_EDGE_INSET) {
    return data;
  }

  arrowOffsets[side] = Math.min(Math.max(arrowOffsets[side], ARROW_EDGE_INSET), max);
  return data;
};

const AnchoredFixedPosition = ({
  target,
  children,
  className,
  placement = 'bottom',
  eventsEnabled,
  usePortal,
  arrowClassName,
  modifiers,
  ...rest
}: AnchoredFixedPositonProps) => {
  if (!target) {
    return null;
  }
  return (
    <AppPortal usePortal={usePortal} className={className}>
      <Popper
        target={target}
        eventsEnabled={eventsEnabled}
        positionFixed
        placement={placement}
        modifiers={
          arrowClassName
            ? {
                // Order 550 runs between Popper's `arrow` (500) and `flip` (600)
                // modifiers, so the clamped offsets survive placement flips.
                keepArrowWithinBounds: {order: 550, enabled: true, fn: keepArrowWithinBounds},
                ...modifiers
              }
            : modifiers
        }
        {...rest}
      >
        {typeof children === 'function' ? children() : children}
        {arrowClassName && <Arrow className={arrowClassName} />}
      </Popper>
    </AppPortal>
  );
};

export default AnchoredFixedPosition;
